import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Chip from '@mui/material/Chip';

interface Project {
  id: number;
  name: string;
  user_id: number;
  created_at: string;
  dataset_count: number;
}

interface ProjectManagerProps {
  onProjectSelect: (project: Project | null) => void;
  selectedProject: Project | null;
  isGuest?: boolean;
  onOpenSample?: () => void;
  onSignUp?: () => void;
}

const ProjectManager: React.FC<ProjectManagerProps> = ({ onProjectSelect, selectedProject, isGuest, onOpenSample, onSignUp }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/projects', { credentials: 'include' });
      if (res.ok) {
        setProjects(await res.json());
      } else {
        const data = await res.json();
        setError(Array.isArray(data.detail) ? data.detail.map((e: any) => e.msg).join(', ') : data.detail || 'Failed to load projects');
      }
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const handleCreate = async () => {
    if (!newProjectName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newProjectName.trim() }),
      });
      if (res.ok) {
        const newProject = await res.json();
        setProjects([...projects, newProject]);
        setNewProjectName('');
        setOpenDialog(false);
        onProjectSelect(newProject);
      } else {
        const data = await res.json();
        setError(Array.isArray(data.detail) ? data.detail.map((e: any) => e.msg).join(', ') : data.detail || 'Failed to create project');
      }
    } catch {
      setError('Network error.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.name}" and all its datasets? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/projects/${project.id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setProjects(projects.filter(p => p.id !== project.id));
        if (selectedProject?.id === project.id) onProjectSelect(null);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to delete project');
      }
    } catch {
      setError('Network error.');
    }
  };

  return (
    <Box>
      {/* Sign-up prompt dialog for guests */}
      <Dialog open={showSignUpPrompt} onClose={() => setShowSignUpPrompt(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#F3F4F6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <FolderOpenIcon sx={{ fontSize: 22, color: '#6B7280' }} />
          </Box>
          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, color: '#111111', mb: 1 }}>
            Create a free account
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', lineHeight: 1.6, mb: 3 }}>
            You're exploring as a guest. Sign up for free to create projects, upload your own datasets, and save your work.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => { setShowSignUpPrompt(false); onSignUp?.(); }}
            sx={{ bgcolor: '#111111', color: '#fff', borderRadius: '8px', textTransform: 'none',
              fontWeight: 600, fontSize: '0.9375rem', py: 1.25, mb: 1.5,
              '&:hover': { bgcolor: '#374151' } }}>
            Sign Up — It's Free
          </Button>
          <Button variant="text" fullWidth onClick={() => setShowSignUpPrompt(false)}
            sx={{ color: '#9CA3AF', textTransform: 'none', fontSize: '0.875rem' }}>
            Continue as Guest
          </Button>
        </DialogContent>
      </Dialog>

      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 1.5, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 500, mb: 0.25 }}>
            {projects.length} project{projects.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => isGuest ? setShowSignUpPrompt(true) : setOpenDialog(true)}
          sx={{
            backgroundColor: '#111111',
            color: '#FFFFFF',
            fontSize: '0.875rem',
            '&:hover': { backgroundColor: '#2D2D2D' },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          New Project
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Project list */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          [1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 2.5,
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '6px', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width={180} height={18} sx={{ mb: 0.5 }} />
                <Skeleton width={100} height={14} />
              </Box>
            </Box>
          ))
        ) : projects.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <FolderOpenIcon sx={{ fontSize: 40, color: '#E5E7EB', mb: 2 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#111111', mb: 0.75 }}>
              No projects yet
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#9CA3AF', mb: 3, maxWidth: 320, mx: 'auto' }}>
              Projects help you organize your datasets. Create your first one to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => isGuest ? setShowSignUpPrompt(true) : setOpenDialog(true)}
              sx={{ backgroundColor: '#111111', color: '#FFFFFF' }}
            >
              Create First Project
            </Button>
          </Box>
        ) : (
          projects.map((project, index) => {
            // Guests: replace "Sample Sales Data" project row with yellow sample card
            if (isGuest && project.name === 'Sample Sales Data') {
              return (
                <Box
                  key={project.id}
                  onClick={onOpenSample}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 3, py: 2.25,
                    backgroundColor: '#FEFCE8',
                    borderBottom: index < projects.length - 1 ? '1px solid #FDE68A' : 'none',
                    cursor: 'pointer', transition: 'background-color 0.12s ease',
                    '&:hover': { backgroundColor: '#FEF9C3' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #FDE68A 0%, #FCD34D 100%)',
                        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <TrendingUpIcon sx={{ fontSize: 16, color: '#92400E' }} />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#92400E' }}>
                          Sample Dataset
                        </Typography>
                        <Chip label="Try it" size="small"
                          sx={{ bgcolor: '#FDE68A', color: '#92400E', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', color: '#A16207' }}>
                        1,000 rows of sales data — explore charts, filters, and AI
                      </Typography>
                    </Box>
                  </Box>
                  <ArrowForwardIcon sx={{ fontSize: 16, color: '#D97706', flexShrink: 0 }} />
                </Box>
              );
            }
            return (
            <Box
              key={project.id}
              onClick={() => onProjectSelect(project)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 2.25,
                borderBottom: index < projects.length - 1 ? '1px solid #F3F4F6' : 'none',
                cursor: 'pointer',
                backgroundColor: selectedProject?.id === project.id ? '#F9FAFB' : '#FFFFFF',
                transition: 'background-color 0.12s ease',
                '&:hover': { backgroundColor: '#F9FAFB' },
                '&:hover .delete-btn': { opacity: 1 },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: selectedProject?.id === project.id ? '#111111' : '#F3F4F6',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background-color 0.12s ease',
                }}
              >
                <FolderOpenIcon
                  sx={{
                    fontSize: 16,
                    color: selectedProject?.id === project.id ? '#FFFFFF' : '#6B7280',
                  }}
                />
              </Box>

              {/* Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    fontWeight: selectedProject?.id === project.id ? 600 : 500,
                    color: '#111111',
                    mb: 0.25,
                  }}
                  noWrap
                >
                  {project.name}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                  {project.dataset_count} dataset{project.dataset_count !== 1 ? 's' : ''} ·{' '}
                  {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Tooltip title="Delete project">
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => handleDelete(project, e)}
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.12s ease',
                      color: '#9CA3AF',
                      '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <ArrowForwardIcon sx={{ fontSize: 16, color: '#D1D5DB' }} />
              </Box>
            </Box>
            );
          })
        )}
      </Box>

      {/* Create dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setNewProjectName(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>New Project</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280', mb: 2 }}>
            Give your project a name. You can organize multiple datasets inside it.
          </Typography>
          <TextField
            label="Project name"
            value={newProjectName}
            onChange={e => setNewProjectName(e.target.value)}
            fullWidth
            autoFocus
            placeholder="e.g. Q1 Sales Analysis"
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setNewProjectName(''); }} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating || !newProjectName.trim()}
            sx={{ backgroundColor: '#111111', color: '#FFFFFF' }}
          >
            {creating ? 'Creating…' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectManager;
